/**
 * L'ABRÉVIATION D'UN POSTE dans une case du planning mensuel (29/09/2026).
 *
 * Retour de la RQ : « le calendrier mensuel par personne n'affiche pas les
 * postes occupés par chaque personne ». Il les affichait — tronqués à quatre
 * caractères. Or les quatre accueils (ACCUEIL1, ACCUEIL2, ACCUEIL3, ACCUEILPM)
 * y devenaient tous « ACCU », et les trois techniques du dimanche « DIMT » : la
 * case ne disait plus QUEL poste. Une abréviation qui confond deux postes
 * n'informe pas, elle trompe.
 *
 * ── Deux règles ─────────────────────────────────────────────────────────────
 *
 *  1. Une abréviation LISIBLE pour les postes connus du laboratoire (A1, PLV+,
 *     P5, Garde…), sinon le code sans ses séparateurs.
 *  2. ⚠️ JAMAIS DEUX POSTES SOUS LA MÊME ABRÉVIATION : en cas de collision, on
 *     rallonge jusqu'à ce qu'elles diffèrent, et au pire on rend le code
 *     entier. La largeur de la case cède devant l'exactitude, pas l'inverse.
 *
 * Module PUR : aucun import d'exécution — testé par `npm test` (backend).
 */

const CONNUS: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^ACCUEIL(\d+)$/, (m) => `A${m[1]}`],
  [/^ACCUEILPM$/, () => 'A+'],
  [/^PLV(\d+)$/, (m) => `PLV${m[1]}`],
  [/^PLVPM$/, () => 'PLV+'],
  [/^P(\d+)_/, (m) => `P${m[1]}`],
  [/^GARDE$/, () => 'G'], // « G » simplement (RQ, 30/09/2026)
  [/^DIMTECHPM$/, () => 'DT pm'],
  [/^DIMTECH(\d*)$/, (m) => `DT${m[1] || '1'}`],
  [/^DIMBACT$/, () => 'DBact'],
  [/^DIMRECEP$/, () => 'DRécep'],
  [/^ENTRETIEN$/, () => 'Entr'],
  [/^COURSIER$/, () => 'Cours'],
  [/^BIOLOGISTE$/, () => 'Bio'],
  [/^RMQ_SUPPL$/, () => 'RMQs'],
];

/** L'abréviation « naturelle » d'un code, avant tout arbitrage de collision. */
export function abregePoste(code: string): string {
  const c = String(code || '').trim().toUpperCase();
  if (!c) return '?';
  for (const [re, f] of CONNUS) {
    const m = c.match(re);
    if (m) return f(m);
  }
  const sans = c.replace(/[^A-Z0-9]/g, '');
  return sans.length <= 5 ? sans : sans.slice(0, 5);
}

/**
 * Les abréviations d'un ENSEMBLE de postes, garanties distinctes.
 * Rend `code → abréviation`.
 */
export function abregesPostes(codes: string[]): Map<string, string> {
  const uniques = Array.from(new Set(codes.filter(Boolean)));
  const out = new Map(uniques.map((c) => [c, abregePoste(c)]));
  // Tant qu'une abréviation est partagée, les postes en cause reprennent leur
  // code entier : c'est long, mais c'est vrai.
  for (let tour = 0; tour < 3; tour += 1) {
    const vus = new Map<string, string[]>();
    for (const [c, a] of out) vus.set(a, [...(vus.get(a) || []), c]);
    let collision = false;
    for (const [, cs] of vus) {
      if (cs.length < 2) continue;
      collision = true;
      for (const c of cs) out.set(c, c);
    }
    if (!collision) break;
  }
  return out;
}
